import {
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Clock3,
  HandHeart,
  HeartHandshake,
  MapPin,
  MessageCircleHeart,
  Sparkles,
} from 'lucide-react';
import { imgCongregacionWide } from '../assets';
import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { useNextService, useReveal } from '../hooks';
import { churchAddress, mapEmbedUrl, mapUrl, services } from '../site';

const expectations = [
  {
    icon: HeartHandshake,
    number: '01',
    title: 'Te recibimos como familia',
    text: 'Desde la puerta alguien te dará la bienvenida y te ayudará a encontrar tu lugar. Ven tal como eres.',
  },
  {
    icon: Sparkles,
    number: '02',
    title: 'Adoración y Palabra',
    text: 'Vivimos un tiempo de alabanza genuina y un mensaje bíblico, práctico y lleno de esperanza.',
  },
  {
    icon: HandHeart,
    number: '03',
    title: 'Oramos por ti',
    text: 'Al final de cada servicio hay un momento para orar. Si lo deseas, te acompañamos en oración.',
  },
];

const faqs = [
  {
    question: '¿Tengo que vestir de alguna forma especial?',
    answer:
      'No. Ven como te sientas cómodo: en la casa encontrarás personas de todo estilo. Lo importante es que vengas.',
  },
  {
    question: '¿Puedo llevar a mis hijos?',
    answer:
      '¡Claro! Los niños son bienvenidos en todos nuestros servicios. Trae a toda la familia, hay un lugar para cada uno.',
  },
  {
    question: '¿Necesito ser miembro o registrarme para asistir?',
    answer:
      'No necesitas registrarte ni ser miembro. Todos nuestros servicios son abiertos; llega unos minutos antes y siéntete en casa.',
  },
  {
    question: '¿Cuánto dura un servicio?',
    answer:
      'Aproximadamente hora y media. Incluye adoración, el mensaje y un momento de oración al final.',
  },
];

function NextServiceBanner() {
  const next = useNextService();

  return (
    <div className="next-service-banner reveal">
      <span className="pulse-dot" aria-hidden="true" />
      <p>
        Próximo encuentro: <strong>{next.service.day} {next.service.time}</strong> · {next.service.title}
      </p>
      <span className="next-service-countdown">{next.when} · {next.countdown}</span>
    </div>
  );
}

export function VisitaPage() {
  useReveal();

  return (
    <Layout>
      <PageHero
        kicker="Planifica tu visita"
        title={<>Tu primera visita, <em>sin complicaciones</em>.</>}
        lead="Queremos que llegar por primera vez se sienta sencillo. Aquí tienes todo lo que necesitas: qué esperar, horarios y cómo llegar."
        image={imgCongregacionWide}
      >
        <a className="button button-gold" href={mapUrl} target="_blank" rel="noopener noreferrer">
          Abrir en Google Maps <ArrowUpRight size={18} aria-hidden="true" />
        </a>
      </PageHero>

      <section className="section-space" aria-labelledby="expect-title">
        <div className="page-shell">
          <div className="section-heading reveal">
            <div>
              <p className="section-kicker">
                <span /> Qué esperar
              </p>
              <h2 id="expect-title">Así se siente un domingo con nosotros.</h2>
            </div>
            <p>
              Un ambiente cercano, sin protocolos complicados. Llega como estás;
              nosotros ponemos la bienvenida.
            </p>
          </div>
          <div className="expect-grid">
            {expectations.map(({ icon: Icon, number, title, text }) => (
              <article className="visit-step reveal" key={number}>
                <div className="step-topline">
                  <span className="step-icon">
                    <Icon size={22} aria-hidden="true" />
                  </span>
                  <span className="step-number">{number}</span>
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="visit-schedule section-space" aria-labelledby="schedule-title">
        <div className="page-shell">
          <div className="section-heading reveal">
            <div>
              <p className="section-kicker">
                <span /> Horarios
              </p>
              <h2 id="schedule-title">Elige tu encuentro.</h2>
            </div>
            <NextServiceBanner />
          </div>
          <div className="schedule-list">
            {services.map((service) => (
              <article className="schedule-row reveal" key={`${service.day}-${service.time}`}>
                <div className="schedule-day">
                  <CalendarDays size={20} aria-hidden="true" />
                  <strong>{service.day}</strong>
                </div>
                <time>
                  <Clock3 size={18} aria-hidden="true" /> {service.time}
                </time>
                <div className="schedule-info">
                  <h3>{service.title}</h3>
                  <p>{service.note}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-space" aria-labelledby="location-title">
        <div className="page-shell location-layout">
          <div className="location-copy reveal">
            <p className="section-kicker">
              <span /> Cómo llegar
            </p>
            <h2 id="location-title">Estamos en el corazón de Juárez.</h2>
            <p>
              Abre la ruta antes de salir y llega unos minutos antes del servicio.
              Te esperamos con la puerta abierta.
            </p>
            <a
              className="visit-detail visit-detail-link"
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>
                <MapPin size={21} aria-hidden="true" />
              </span>
              <div>
                <small>Dirección</small>
                <strong>{churchAddress.street}</strong>
                <p>{churchAddress.locality}</p>
              </div>
              <ArrowUpRight className="visit-detail-arrow" size={18} aria-hidden="true" />
            </a>
            <a className="button button-primary" href={mapUrl} target="_blank" rel="noopener noreferrer">
              Cómo llegar <ArrowUpRight size={18} aria-hidden="true" />
            </a>
          </div>
          <div className="location-map reveal">
            <iframe
              title="Mapa de Iglesia Cristiana Tercer Cielo"
              src={mapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className="faq-section section-space" aria-labelledby="faq-title">
        <div className="page-shell faq-layout">
          <div className="faq-intro reveal">
            <p className="section-kicker">
              <span /> Preguntas frecuentes
            </p>
            <h2 id="faq-title">¿Primera vez? Tranquilo.</h2>
            <p>
              Estas son las preguntas que más nos hacen quienes nos visitan por
              primera vez. Si tienes otra, <a className="text-link" href="/contacto/">escríbenos</a>.
            </p>
          </div>
          <div className="faq-list">
            {faqs.map((faq) => (
              <details className="faq-item reveal" key={faq.question}>
                <summary>
                  <MessageCircleHeart size={20} aria-hidden="true" />
                  <h3>{faq.question}</h3>
                  <span className="faq-chevron" aria-hidden="true" />
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band" aria-labelledby="visit-cta-title">
        <div className="page-shell cta-band-inner reveal">
          <h2 id="visit-cta-title">Nos vemos este {services[0].day}.</h2>
          <p>Guarda la dirección, elige tu horario y ven a casa.</p>
          <div className="cta-band-actions">
            <a className="button button-light" href={mapUrl} target="_blank" rel="noopener noreferrer">
              Abrir en Google Maps <ArrowUpRight size={18} aria-hidden="true" />
            </a>
            <a className="button button-ghost" href="/contacto/">
              Escríbenos <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
